import { Injectable } from '@angular/core';
import 'rxjs/add/observable/forkJoin';
import 'rxjs/add/operator/do';
import 'rxjs/add/operator/map';
import { Observable } from 'rxjs/Observable';
import { Subject } from 'rxjs/Subject';
import { Customer } from '../models/customer.model';
import { DashboardViewModel } from '../models/dashboard-view-model';
import { Permission, PermissionValues } from '../models/permission.model';
import { Role } from '../models/role.model';
import { UserEdit } from '../models/user-edit.model';
import { User } from '../models/user.model';
import { AccountEndpoint } from './account-endpoint.service';
import { AuthService } from './auth.service';
import { DBkeys } from './db-Keys';

export type RolesChangedOperation = "add" | "delete" | "modify";
export type RolesChangedEventArg = { roles: Role[] | string[], operation: RolesChangedOperation };

@Injectable()
export class AccountService {

    public static readonly roleAddedOperation: RolesChangedOperation = "add";
    public static readonly roleDeletedOperation: RolesChangedOperation = "delete";
    public static readonly roleModifiedOperation: RolesChangedOperation = "modify";
    public appTrialDays: any;

    private _rolesChanged = new Subject<RolesChangedEventArg>();
    applicationVersion: any;
    applicationVersionName: any;

    constructor(private authService: AuthService, private accountEndpoint: AccountEndpoint) {
        this.getApplicationVersion();
        this.getApplicationVersionName();
        this.getApplicationTrialDays();
    }

    getUserExpireExtend(userId?: string, date?: string) {
        return this.accountEndpoint.getUserExpireExtendEndpoint(userId, DBkeys.EXTEND_YEARS, date)
            .map((response: Response) => response);
    }

    getMarkAsDeleteEndpoint(userId?: string) {
        return this.accountEndpoint.getMarkAsDeleteEndpoint(userId)
            .map((response: Response) => response);
    }

    sendPasswordResetlink(email: string) {
        return this.accountEndpoint.sendPasswordResetlink(email);
    }

    verifyChangePasswordIdentity(id: string, passwordVerificationKey: string) {
        return this.accountEndpoint.verifyChangePasswordIdentity(id, passwordVerificationKey);
    }

    resetPassword(id: string, newPassword: string) {
        return this.accountEndpoint.resetPassword(id, newPassword);
    }

    getUserUpdatePassword(userId?: string, newPassword?: string) {
        return this.accountEndpoint.getUserUpdatePasswordEndpoint(userId, newPassword);
    }
    getApplicationVersion() {
        const version = this.accountEndpoint.getApplicationVersion().subscribe(response => {
            this.applicationVersion = response;
        });
    }

    getApplicationVersionName() {
        const versionName = this.accountEndpoint.getApplicationVersionName().subscribe(res => {
            this.applicationVersionName = res;
        });
    }


    getApplicationTrialDays() {
        const trialDays = this.accountEndpoint.getAppTrialDays().subscribe(res => {
            this.appTrialDays = parseInt(String(res));
            return parseInt(res);
        });
    }

    getDashboardDates() {
        return this.accountEndpoint.getDashboardDates<DashboardViewModel>();
    }

    getTempApplicationTrialDays() {
        return this.accountEndpoint.getAppTrialDays();
    }

    getUser(userId?: string) {
        return this.accountEndpoint.getUserEndpoint<UserEdit>(userId);
    }

    getUserAndRoles(userId?: string) {
        return Observable.forkJoin(
            this.accountEndpoint.getUserEndpoint<User>(userId),
            this.accountEndpoint.getRolesEndpoint<Role[]>());
    }

    getUsers(page?: number, pageSize?: number) {
        return this.accountEndpoint.getUsersEndpoint<User[]>(page, pageSize);
    }

    getUsersAndRoles(page?: number, pageSize?: number) {
        return Observable.forkJoin(
            this.accountEndpoint.getUsersEndpoint<User[]>(page, pageSize),
            this.accountEndpoint.getRolesEndpoint<Role[]>());
    }

    updateUser(user: UserEdit) {
        const tempu: any = user;
        if (tempu.Id) {
            return this.accountEndpoint.getUpdateUserEndpoint(user, tempu.Id);
        } else {
            return this.accountEndpoint.getUserByUserNameEndpoint<User>(tempu.UserName)
                .mergeMap(foundUser => {
                    user.Id = foundUser.Id;
                    return this.accountEndpoint.getUpdateUserEndpoint(user, tempu.Id);
                });
        }
    }

    newUser(user: UserEdit) {
        return this.accountEndpoint.getNewUserEndpoint<User>(user);
    }

    newPublicUser(user: UserEdit) {
        return this.accountEndpoint.getPublicNewUserEndpoint<User>(user);
    }

    addUser(customer: Customer) {
        return this.accountEndpoint.getAddUserEndpoint<Customer>(customer);
    }

    getUserPreferences() {
        return this.accountEndpoint.getUserPreferencesEndpoint<string>();
    }

    updateUserPreferences(configuration: string) {
        return this.accountEndpoint.getUpdateUserPreferencesEndpoint(configuration);
    }

    deleteUser(userOrUserId: string | UserEdit): Observable<User> {
        if (typeof userOrUserId === 'string' || userOrUserId instanceof String) {
            return this.accountEndpoint.getDeleteUserEndpoint<User>(<string>userOrUserId)
                .do(data => this.onRolesUserCountChanged(data.Roles));
        } else {
            if (userOrUserId.Id) {
                return this.deleteUser(userOrUserId.Id);
            } else {
                return this.accountEndpoint.getUserByUserNameEndpoint<User>(userOrUserId.UserName)
                    .mergeMap(user => this.deleteUser(user.Id));
            }
        }
    }

    unblockUser(userId: string) {
        return this.accountEndpoint.getUnblockUserEndpoint(userId);
    }

    userHasPermission(permissionValue: PermissionValues): boolean {
        return this.permissions.some(p => p == permissionValue);
    }

    refreshLoggedInUser() {
        return this.authService.refreshLogin();
    }

    getRoles(page?: number, pageSize?: number) {

        return this.accountEndpoint.getRolesEndpoint<Role[]>(page, pageSize);
    }

    getRolesAndPermissions(page?: number, pageSize?: number) {
        return Observable.forkJoin(
            this.accountEndpoint.getRolesEndpoint<Role[]>(page, pageSize),
            this.accountEndpoint.getPermissionsEndpoint<Permission[]>());
    }

    updateRole(role: Role) {
        if (role.Id) {
            return this.accountEndpoint.getUpdateRoleEndpoint(role, role.Id)
                .do(() => this.onRolesChanged([role], AccountService.roleModifiedOperation));
        } else {
            return this.accountEndpoint.getRoleByRoleNameEndpoint<Role>(role.Name)
                .mergeMap(foundRole => {
                    role.Id = foundRole.Id;
                    return this.accountEndpoint.getUpdateRoleEndpoint(role, role.Id);
                })
                .do(() => this.onRolesChanged([role], AccountService.roleModifiedOperation));
        }
    }

    newRole(role: Role) {
        return this.accountEndpoint.getNewRoleEndpoint<Role>(role)
            .do(() => this.onRolesChanged([role], AccountService.roleAddedOperation));
    }

    deleteRole(roleOrRoleId: string | Role): Observable<Role> {
        if (typeof roleOrRoleId === 'string' || roleOrRoleId instanceof String) {
            return this.accountEndpoint.getDeleteRoleEndpoint<Role>(<string>roleOrRoleId)
                .do(data => this.onRolesChanged([data], AccountService.roleDeletedOperation));
        } else {
            if (roleOrRoleId.Id) {
                return this.deleteRole(roleOrRoleId.Id);
            } else {
                return this.accountEndpoint.getRoleByRoleNameEndpoint<Role>(roleOrRoleId.Name)
                    .mergeMap(role => this.deleteRole(role.Id));
            }
        }
    }

    getPermissions() {
        return this.accountEndpoint.getPermissionsEndpoint<Permission[]>();
    }

    private onRolesChanged(roles: Role[] | string[], op: RolesChangedOperation) {
        this._rolesChanged.next({ roles: roles, operation: op });
    }

    onRolesUserCountChanged(roles: Role[] | string[]) {
        return this.onRolesChanged(roles, AccountService.roleModifiedOperation);
    }

    getRolesChangedEvent(): Observable<RolesChangedEventArg> {
        return this._rolesChanged.asObservable();
    }

    get permissions(): PermissionValues[] {
        return this.authService.userPermissions;
    }

    get currentUser() {
        return this.authService.currentUser;
    }
}



// WEBPACK FOOTER //
// ./node_modules/angular2-template-loader!./ClientApp/app/services/account.service.ts